import { useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { useToast } from "@/components/base/Toast";
import { addDossier, removeDossier, updateDossier } from "@/services/ficheService";
import type { DossierItem } from "@/types/fiche";

interface Props {
  open: boolean;
  onClose: () => void;
  dossiers: DossierItem[];
  onChanged: () => void;
}

export default function DossierLibraryModal({ open, onClose, dossiers, onChanged }: Props) {
  const { notify } = useToast();
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    const value = label.trim();
    if (!value) {
      notify("Saisissez le libellé du dossier.", "error");
      return;
    }
    setBusy(true);
    try {
      await addDossier(value);
      setLabel("");
      onChanged();
      notify("Pièce ajoutée au catalogue : cochez-la dans la fiche des classes concernées.");
    } catch (err) {
      notify(err instanceof Error ? err.message : "Ajout impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await removeDossier(id);
      onChanged();
      notify("Pièce retirée du catalogue.", "info");
    } catch (err) {
      notify(err instanceof Error ? err.message : "Suppression impossible.", "error");
    }
  };

  const toggleEverywhere = async (d: DossierItem) => {
    try {
      await updateDossier(d.id, { appliesToAllClasses: !d.appliesToAllClasses, classIds: [] });
      onChanged();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Modification impossible.", "error");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Gérer les dossiers"
      subtitle="Catalogue des pièces pouvant être exigées aux élèves"
      icon="ri-folder-settings-line"
      size="md"
      footer={
        <Button variant="outline" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1.5 block text-xs font-medium text-foreground-700">
              Nouveau dossier
            </label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
              placeholder="ex. 2 photos d'identité, attestation de transfert..."
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <Button icon="ri-add-line" disabled={busy} onClick={handleAdd}>
            Ajouter
          </Button>
        </div>

        <div className="space-y-2">
          {dossiers.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between gap-3 rounded-md border border-background-200 px-3.5 py-2.5"
            >
              <span className="flex items-center gap-2 text-sm text-foreground-800">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                  <i className="ri-file-list-3-line text-base"></i>
                </span>
                {d.label}
              </span>
              <label className="ml-auto flex items-center gap-1.5 whitespace-nowrap text-xs text-foreground-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={d.appliesToAllClasses}
                  onChange={() => toggleEverywhere(d)}
                  className="h-3.5 w-3.5 accent-primary-500 cursor-pointer"
                />
                Exigée partout
              </label>
              <button
                type="button"
                onClick={() => handleRemove(d.id)}
                aria-label={`Supprimer ${d.label}`}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-accent-50 hover:text-accent-600 cursor-pointer"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          ))}
          {dossiers.length === 0 && (
            <p className="rounded-md border border-dashed border-background-300 py-6 text-center text-xs text-foreground-400">
              Aucun dossier dans le catalogue.
            </p>
          )}
        </div>
        <p className="text-xs text-foreground-400">
          « Exigée partout » : la pièce est demandée dans toutes les classes. Sinon, cochez-la dans la fiche
          des classes concernées. Retirer une pièce la retire de toutes les classes.
        </p>
      </div>
    </Modal>
  );
}